import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { money2 } from "./utils";

/**
 * Shows the individual NDS rows that contribute to a combined line (e.g. Donations
 * and Sam's delivery are collapsed to one row in view mode; clicking opens this
 * dialog to see the per-location breakdown).
 */
export default function BreakdownDialog({
  open, title, items = [], groups, sign = "+", onClose,
}) {
  const hasGroups = Array.isArray(groups) && groups.length > 0;
  const groupTotal = (g) =>
    (g.items || []).reduce((a, b) => a + (Number(b.amount) || 0), 0);
  const total = hasGroups
    ? groups.reduce((a, g) => a + groupTotal(g), 0)
    : items.reduce((a, b) => a + (Number(b.amount) || 0), 0);
  const amtColor = sign === "-" ? "#dc2626" : "#059669";

  const renderItem = (it, i, last) => (
    <div
      key={it._id || i}
      style={{
        display: "flex", justifyContent: "space-between",
        padding: "10px 0",
        borderBottom: last ? "none" : "1px solid #e5e7eb",
        fontSize: 13,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ color: "#1e293b", fontWeight: 600 }}>{it.description}</span>
        {it.locationName && (
          <span style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>
            {it.locationName}
          </span>
        )}
      </div>
      <span style={{ color: amtColor, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
        {sign}{money2(it.amount)}
      </span>
    </div>
  );

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderRadius: 14,
          fontFamily: "'DM Sans', system-ui, sans-serif",
          minWidth: 340,
          maxWidth: 480,
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)",
        },
      }}
    >
      <DialogTitle
        disableTypography
        style={{
          padding: "20px 24px 10px",
          fontSize: 18,
          fontWeight: 700,
          color: "#1e293b",
          fontFamily: "inherit",
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent style={{ padding: "0 24px 8px" }}>
        {hasGroups ? (
          <div>
            {groups.map((g, gi) => {
              const gItems = g.items || [];
              if (gItems.length === 0) return null;
              const gTotal = groupTotal(g);
              return (
                <div key={g.id || g.title || gi} style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#1e293b",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      paddingTop: gi === 0 ? 4 : 0,
                      paddingBottom: 4,
                    }}
                  >
                    {g.title}
                  </div>
                  {gItems.map((it, i) =>
                    renderItem(it, i, i === gItems.length - 1)
                  )}
                  <div
                    style={{
                      display: "flex", justifyContent: "space-between",
                      marginTop: 6, paddingTop: 6,
                      borderTop: "1px solid #e5e7eb",
                      fontSize: 13, fontWeight: 700,
                    }}
                  >
                    <span style={{ color: "#1e293b" }}>Total</span>
                    <span style={{ color: amtColor, fontVariantNumeric: "tabular-nums" }}>
                      {sign}{money2(gTotal)}
                    </span>
                  </div>
                </div>
              );
            })}
            <div
              style={{
                display: "flex", justifyContent: "space-between",
                marginTop: 4, paddingTop: 10,
                borderTop: "2px solid #1e293b",
                fontSize: 14, fontWeight: 700,
              }}
            >
              <span style={{ color: "#1e293b" }}>Grand Total</span>
              <span style={{ color: amtColor, fontVariantNumeric: "tabular-nums" }}>
                {sign}{money2(total)}
              </span>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: 13, padding: "10px 0" }}>
            No entries.
          </div>
        ) : (
          <div>
            {items.map((it, i) => renderItem(it, i, i === items.length - 1))}
            <div
              style={{
                display: "flex", justifyContent: "space-between",
                marginTop: 10, paddingTop: 10,
                borderTop: "2px solid #e5e7eb",
                fontSize: 14, fontWeight: 700,
              }}
            >
              <span style={{ color: "#1e293b" }}>Total</span>
              <span style={{ color: amtColor, fontVariantNumeric: "tabular-nums" }}>
                {sign}{money2(total)}
              </span>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "10px 20px 16px" }}>
        <Button
          onClick={onClose}
          disableElevation
          style={{
            background: "#2d5a3d",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "8px 20px",
            fontWeight: 600,
            fontSize: 13,
            fontFamily: "inherit",
            textTransform: "none",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
